import { z } from 'zod';
// ─── Evaluation ──────────────────────────────────────────────────────────────
/**
 * `CANCELLED` is the terminal value produced by `POST /v1/evaluations/:id/cancel`
 * (DEV-15928 / EU-74). It mirrors the Prisma `EvaluationStatus` enum, which has
 * carried this value since the cancel endpoint shipped — the Zod schema was
 * just out of sync until the cancel button needed it on the frontend.
 */
export const EvaluationStatusSchema = z.enum([
    'PENDING',
    'RUNNING',
    'COMPLETED',
    'FAILED',
    'CANCELLED',
]);
/**
 * Discriminates workflow-target evals (the pre-existing behavior) from
 * KG-target evals. Keeps both surfaces on the same `Evaluation` table
 * instead of forking; when `KG`, `documentSetId` is set and the eval
 * runs against a LightRAG workspace instead of spawning workflow Runs.
 *
 * `MEMORY` (DEV-17245) reuses the same machinery for memory-retrieval
 * probes: each Task is one probe against an agent's memory store
 * (`MemoryTaskInputSchema` / `MemoryTaskExpectedOutputSchema`), scored by
 * recall@k / nDCG with every miss attributed retrieval-miss vs write-miss.
 * Like `KG`, no workflow Runs are spawned.
 */
export const EvaluationTargetKindSchema = z.enum(['WORKFLOW', 'KG', 'MEMORY']);
/**
 * KG-scoped block embedded under `Evaluation.scoreSummary.kg` when
 * `targetKind === 'KG'`. Kept as an optional block so the existing
 * workflow-eval `scoreSummary` shape stays untouched and existing readers
 * ignore it. Numbers are 0..1 (rates) or nullable when the mode is not
 * exercised by the eval's ground-truth set.
 */
export const KgScoreSummarySchema = z.object({
    /** Number of KG ground-truth entries evaluated. */
    totalItems: z.number().int().min(0),
    /** Items that passed all applied graders. */
    passedItems: z.number().int().min(0),
    /** Mean composite grader score across items (0..1). */
    meanScore: z.number().min(0).max(1),
    /** Per-grader mean scores. Keys are grader names (`kg_answer_cited_recall`,
     *  `kg_entity_recall`, …). */
    perGrader: z.record(z.string(), z.number().min(0).max(1)),
    /** Per-tag pass rates when tasks carry tag metadata. */
    perTag: z.record(z.string(), z.number().min(0).max(1)).optional(),
    /** LightRAG workspace token usage delta captured across the run
     *  (`{ inputTokens, outputTokens, costUsd }`). */
    workspaceUsageDelta: z
        .object({
        inputTokens: z.number().int().min(0).nullable(),
        outputTokens: z.number().int().min(0).nullable(),
        costUsd: z.string().nullable(),
    })
        .optional(),
});
export const EvalProgressSchema = z
    .object({ completed: z.number(), total: z.number(), failed: z.number() })
    .nullable();
export const EvaluationSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    /** Null for KG-target evals — KG evals don't spawn workflow runs.
     *  Still required for WORKFLOW evals. */
    workflowId: z.string().nullable(),
    /** Optional pin to a specific WorkflowVersion. `null` means the eval runs
     *  against the workflow's `currentVersion` at execution time (not pinned).
     *  The actual version executed per Run is recoverable from the run-history. */
    workflowVersionId: z.string().nullable(),
    /**
     * Id of the experiment this eval was started to test, or `null` for a
     * routine eval against the workflow's mainline version. Derived server-
     * side from the experiment one-off `workflowVersionId` the eval (or its
     * Runs) targeted — mirrors `WorkflowVersion.experimentId`. Lets the eval
     * list/detail link back to the refinement experiment that produced it.
     */
    experimentId: z.string().nullable(),
    status: EvaluationStatusSchema,
    datasetFilter: z.record(z.string(), z.unknown()),
    scoreSummary: z.record(z.string(), z.unknown()).nullable(),
    regressionFlags: z.record(z.string(), z.unknown()).nullable(),
    progress: EvalProgressSchema.optional(),
    startedAt: z.date().nullable(),
    finishedAt: z.date().nullable(),
    createdAt: z.date(),
    /**
     * Keycloak subject of whoever created (ran) this evaluation, when a human
     * did. `null` for evals created by an automated principal (e.g. the
     * refinement agent's service token). Resolve against org members to render
     * a "Run by" name; experiment provenance is `experimentId`, never a
     * substitute for this.
     */
    createdBySubject: z.string().nullable(),
    /**
     * KG-target discriminator (see `EvaluationTargetKindSchema`). Legacy
     * workflow-target evals back-fill to `WORKFLOW` via the DB default; the
     * next four fields are `null` on `WORKFLOW` evals and populated on `KG`.
     */
    targetKind: EvaluationTargetKindSchema,
    /** Doc-set the KG eval queries. Null on `WORKFLOW` evals. Opaque metaphysics id. */
    documentSetId: z.string().nullable(),
    /** Optional pin to a specific `DocumentSetKgVersion` (opaque metaphysics id). */
    documentSetKgVersionId: z.string().nullable(),
    /** LightRAG query mode captured for reproducibility. Null on `WORKFLOW`. */
    queryMode: z.string().nullable(),
});
/**
 * Lightweight evaluation shape for list endpoints. Strips the heavy
 * `scoreSummary` and `regressionFlags` JSONB blobs and replaces them
 * with pre-computed summary fields. This keeps the list payload under
 * 1 KB per row instead of 500 KB–5 MB when trials contain transcripts.
 *
 * The full `scoreSummary` is still available via `GET /v1/evaluations/:id`.
 */
export const EvaluationListItemSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    /** Null for KG-target evals. */
    workflowId: z.string().nullable(),
    workflowVersionId: z.string().nullable(),
    experimentId: z.string().nullable(),
    status: EvaluationStatusSchema,
    progress: EvalProgressSchema.optional(),
    startedAt: z.date().nullable(),
    finishedAt: z.date().nullable(),
    createdAt: z.date(),
    createdBySubject: z.string().nullable(),
    /** Target-kind discriminator. The list view needs it because KG and
     *  MEMORY evals both have `workflowId: null` — grouping/badging cannot
     *  be derived from the workflow pointer alone. */
    targetKind: EvaluationTargetKindSchema,
    // ── Lightweight derived fields (computed from scoreSummary) ──
    /** Suite ID (if task-based eval targeted a suite). */
    suiteId: z.string().nullable(),
    /** Trials per task (null for legacy evals). */
    nTrials: z.number().int().nullable(),
    /** Overall pass rate: passed / total (null when no results yet). */
    passRate: z.number().nullable(),
    /** Number of passed items (tasks or GT entries). */
    passedCount: z.number().int().nullable(),
    /** Total items evaluated. */
    totalCount: z.number().int().nullable(),
    /** Number of tasks (null for legacy). */
    taskCount: z.number().int().nullable(),
    /** Mean pass@1 from run-level aggregate (null when unavailable). */
    passAt1: z.number().nullable(),
    /** Free-form labels for grouping/filtering evals (e.g. "intel",
     *  "regression-suite"). Lower-cased, deduped. Defaults to []. */
    tags: z.array(z.string()).default([]),
});
export const CreateEvaluationInputSchema = z.object({
    /**
     * Workflow this evaluation targets. Required for `targetKind='WORKFLOW'`
     * (default) — the eval spawns runs against this workflow. Optional and
     * omitted for `targetKind='KG'` — made KG evals
     * self-contained (Task/Suite rows carry the documentSetId; no carrier
     * workflow needed).
     */
    workflowId: z.string().min(1).optional(),
    /** Optional pin to a specific WorkflowVersion. When set, every Run the
     *  eval spawns targets this version; when omitted, runs fall back to
     *  the workflow's currentVersion at execution time. Used by the
     *  refinement agent to evaluate an experimental one-off version
     *  without promoting it to ACTIVE first. The version must belong to
     *  the same `workflowId` — cross-workflow references are rejected. */
    workflowVersionId: z.string().min(1).optional(),
    /** Selects which ground truth entries to run — arbitrary JSON filter. */
    datasetFilter: z.record(z.string(), z.unknown()).default({}),
    /** Run all Tasks in this Suite. Mutually exclusive with `taskIds`. */
    suiteId: z.string().min(1).optional(),
    /** Run these specific Tasks. Mutually exclusive with `suiteId`. */
    taskIds: z.array(z.string().min(1)).optional(),
    /** Trials per Task. Each Trial = one independent workflow execution.
     *  Must be >= max(reportKs). Default 1. */
    nTrials: z.number().int().min(1).optional(),
    /** Reliability horizons for pass@k / pass^k reporting.
     *  E.g. [1, 3, 5]. Each k must be <= nTrials. Default [1]. */
    reportKs: z.array(z.number().int().min(1)).optional(),
    /** Free-form labels for grouping/filtering (e.g. ["intel"]). Normalized
     *  server-side (trimmed, lower-cased, deduped, empties dropped). */
    tags: z.array(z.string()).optional(),
    // ── KG refinement ────────────────────────────────────────
    /**
     * Target kind — `workflow` (default) preserves pre-refactor behavior;
     * `kg` routes the eval into the KG harness. When `kg`, `documentSetId`
     * is required; `documentSetKgVersionId` and `queryMode` are optional.
     */
    targetKind: EvaluationTargetKindSchema.optional(),
    /** Required when `targetKind='kg'`. Opaque metaphysics doc-set id. */
    documentSetId: z.string().min(1).optional(),
    /** Optional pin to a specific `DocumentSetKgVersion` (opaque). Null
     *  falls back to the doc-set's ACTIVE version at eval time. */
    documentSetKgVersionId: z.string().min(1).optional(),
    /** LightRAG query mode for the eval (`naive` | `local` | `global` |
     *  `hybrid` | `mix`). Optional — the executor picks a fallback when
     *  omitted. */
    queryMode: z.string().min(1).optional(),
});
/**
 * Slim Run summary returned by `GET /v1/evaluations/:id/run-history`. Carries
 * just enough to populate the eval-detail "execution history" sidebar without
 * shipping the per-Run input/output/error JSON blobs across the wire. Drill
 * into a specific row via the existing `GET /v1/runs/:id`.
 */
export const EvaluationRunHistoryItemSchema = z.object({
    id: z.string(),
    status: z.enum(['PENDING', 'RUNNING', 'PAUSED', 'COMPLETED', 'FAILED', 'CANCELLED', 'ABORTED']),
    workflowVersionId: z.string(),
    startedAt: z.date().nullable(),
    finishedAt: z.date().nullable(),
    createdAt: z.date(),
});
/**
 * One point on a workflow's evaluation metric timeline, returned by
 * `GET /v1/evaluations/metrics-timeline?workflowId=`. Powers the
 * accuracy/cost/speed trend chart on `/refinement/overview`.
 *
 * Each metric is independently nullable — an eval that never finished has
 * no `speedMs`, one that wrote no scoreSummary has no `accuracy`, and one
 * with no CostRecords has no `costUsd`. The chart renders gaps for nulls
 * rather than dropping the point.
 *
 *   accuracy      ← Evaluation.scoreSummary.passed / .total          (0-1)
 *   costUsd       ← SUM(CostRecord.usd) over the eval's runs          (TOTAL, string)
 *   runCount      ← number of Runs the eval executed                 (denominator)
 *   avgCostPerRunUsd ← costUsd / runCount — average workflow-run cost    (string)
 *                   per run; normalizes out dataset size so a larger
 *                   ground-truth set doesn't inflate the trend line
 *   speedMs          ← Evaluation.finishedAt − Evaluation.startedAt   (ms, TOTAL)
 *   avgSpeedMsPerRun ← mean of Run.finishedAt − startedAt over the     (ms)
 *                      eval's runs; normalized per-run speed (avg duration)
 *
 * Cost is summed over the eval's Run ids (workflow-run cost only), the same
 * basis the experiment comparison uses, so the two views never disagree.
 *
 * `experiment` is non-null when the evaluation's Runs targeted an
 * Experiment's one-off `workflowVersionId` — i.e. the eval was produced
 * to test a refinement-agent experiment rather than the workflow's
 * mainline. Mirrors how `GET /v1/experiments/:id/comparison` discovers an
 * experiment's candidate eval. Lets the trend chart mark which points came
 * from experiments vs. routine evaluations.
 */
export const EvalMetricsTimelinePointSchema = z.object({
    evaluationId: z.string(),
    createdAt: z.date(),
    status: EvaluationStatusSchema,
    accuracy: z.number().nullable(),
    /** TOTAL workflow-run cost summed over the eval's runs. Retained alongside
     *  the per-run figure for budget visibility / SDK back-compat. */
    costUsd: z.string().nullable(),
    /** Number of Runs the eval executed — the denominator for `avgCostPerRunUsd`. */
    runCount: z.number().int().nullable(),
    /** `costUsd / runCount` — average workflow-run cost per run. Null when no
     *  cost records exist or `runCount` is 0. */
    avgCostPerRunUsd: z.string().nullable(),
    /** TOTAL eval wall-clock (`finishedAt − startedAt`, ms). Retained alongside
     *  the per-run figure; confounded by dataset size + worker concurrency. */
    speedMs: z.number().int().nullable(),
    /** Mean per-run speed (avg run duration): average of `Run.finishedAt −
     *  startedAt` over the eval's runs (ms, rounded). Normalized speed metric,
     *  independent of dataset size and concurrency. Null when no run has both
     *  timestamps. */
    avgSpeedMsPerRun: z.number().int().nullable(),
    /** The experiment this eval tested, or null for a routine (mainline) eval. */
    experiment: z.object({ id: z.string(), name: z.string() }).nullable(),
    /**
     * The workflow version the eval was tested against — resolved from the
     * version the eval's Runs targeted (falling back to the eval's own pin).
     * Null when neither a pin nor any run pins a version.
     */
    workflowVersion: z.object({ id: z.string(), versionNumber: z.number().int() }).nullable(),
});
/** Chronological (oldest-first) series of {@link EvalMetricsTimelinePointSchema}. */
export const EvalMetricsTimelineSchema = z.array(EvalMetricsTimelinePointSchema);
/**
 * Server-computed regression-gate verdict (DEV-16844), persisted on
 * `Evaluation.gateVerdict` at terminal scoreSummary write for gating suites.
 * The strict metric (Q7): a task passes only when ALL its trials pass.
 * INCONCLUSIVE = fail-closed (missing snapshots / missing expectedPassRate).
 */
export const GateVerdictSchema = z.object({
    status: z.enum(['PASSED', 'FAILED', 'INCONCLUSIVE']),
    reason: z.string().optional(),
    passRate: z.number().optional(),
    expectedPassRate: z.number().nullable().optional(),
    passedTasks: z.number().int().optional(),
    totalTasks: z.number().int().optional(),
    computedAt: z.string().optional(),
});
//# sourceMappingURL=eval.js.map